import { ChangeEvent, FormEvent, useState } from 'react'

import AppLayout from '../../components/app-layout/AppLayout'
import Icon from '../../components/icon/Icon'
import { requestLeaseClauseCheck } from '../../_data/renter-llm'
import { LeaseClauseCheckResult } from '../../_types/quickpass-llm'
import {
  PageStack,
  Header,
  PageTitle,
  PageSubtitle,
  FormCard,
  FieldLabel,
  JurisdictionRow,
  JurisdictionSelect,
  TextareaRow,
  LeaseTextarea,
  FormFooter,
  HintText,
  FooterActions,
  SubmitButton,
  ResetButton,
  SummaryCard,
  RiskRow,
  RiskScoreBadge,
  RiskLabel,
  SummaryText,
  JurisdictionContext,
  FindingsList,
  FindingCard,
  FindingHeader,
  FindingCategory,
  SeverityPill,
  Excerpt,
  FindingExplanation,
  NormalLabel,
  NormalText,
  NormalBlock,
  Disclaimer,
  StatusText,
  ErrorBanner,
} from './styled'
import {
  JURISDICTION_OPTIONS,
  buildFingerprint,
  readCache,
  riskLabel,
  riskTone,
  severityGlyph,
  sortFindingsBySeverity,
  writeCache,
} from './helpers'
import { LeaseCheckPageState } from './types'

const MIN_LEASE_LENGTH = 240
const DEFAULT_JURISDICTION = JURISDICTION_OPTIONS[0].value

/**
 * Paste-a-lease, get-a-breakdown page. Calls the lease-clause-check
 * LLM kind, renders severity-sorted findings + risk score + a clear
 * "this isn't legal advice" disclaimer. Caches results 6h per
 * fingerprint so repeat checks are instant + free.
 *
 * @returns {JSX.Element}
 */
const LeaseCheckPage = (): JSX.Element => {
  const [leaseText, setLeaseText] = useState<string>('')
  const [jurisdiction, setJurisdiction] = useState<string>(DEFAULT_JURISDICTION)
  const [state, setState] = useState<LeaseCheckPageState>('idle')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [result, setResult] = useState<LeaseClauseCheckResult | null>(null)

  const handleLeaseChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setLeaseText(event.target.value)
  }

  const handleJurisdictionChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ): void => {
    setJurisdiction(event.target.value)
  }

  const trimmedLease = leaseText.trim()
  const isLongEnough = trimmedLease.length >= MIN_LEASE_LENGTH
  const canSubmit = isLongEnough === true && state !== 'loading'

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()

    if (canSubmit === false) {
      return
    }

    const fingerprint = buildFingerprint(trimmedLease, jurisdiction)
    const cached = readCache(fingerprint)

    if (cached !== null) {
      setResult(cached.result)
      setState('ready')
      return
    }

    setState('loading')
    setErrorMessage('')
    setResult(null)

    try {
      const response = await requestLeaseClauseCheck({
        leaseText: trimmedLease,
        jurisdiction,
      })

      if (response === null) {
        setState('offline')
        return
      }

      writeCache(fingerprint, response)
      setResult(response)
      setState('ready')
    } catch (error) {
      setState('error')
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not check the lease right now. Try again in a sec.',
      )
    }
  }

  const handleReset = (): void => {
    setLeaseText('')
    setJurisdiction(DEFAULT_JURISDICTION)
    setState('idle')
    setResult(null)
    setErrorMessage('')
  }

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.32 } }}
      >
        <Header>
          <PageTitle>Lease check</PageTitle>
          <PageSubtitle>
            Paste a lease — we&apos;ll flag clauses that look risky, unusual, or
            worth negotiating before you sign.
          </PageSubtitle>
        </Header>

        <form onSubmit={handleSubmit}>
          <FormCard>
            <JurisdictionRow>
              <FieldLabel htmlFor="lease-jurisdiction">Jurisdiction</FieldLabel>
              <JurisdictionSelect
                id="lease-jurisdiction"
                value={jurisdiction}
                onChange={handleJurisdictionChange}
              >
                {JURISDICTION_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </JurisdictionSelect>
            </JurisdictionRow>

            <TextareaRow>
              <FieldLabel htmlFor="lease-text">Lease text</FieldLabel>
              <LeaseTextarea
                id="lease-text"
                value={leaseText}
                onChange={handleLeaseChange}
                placeholder="Paste the lease body here. We don't store it on our servers."
                spellCheck={false}
              />
            </TextareaRow>

            <FormFooter>
              <HintText>
                {isLongEnough === true
                  ? `${trimmedLease.length.toLocaleString('en-CA')} characters ready.`
                  : `Need at least ${MIN_LEASE_LENGTH} characters — keep pasting.`}
              </HintText>
              <FooterActions>
                <ResetButton type="button" onClick={handleReset}>
                  Clear
                </ResetButton>
                <SubmitButton type="submit" disabled={canSubmit === false}>
                  {state === 'loading' ? 'Checking…' : 'Check the lease'}
                </SubmitButton>
              </FooterActions>
            </FormFooter>
          </FormCard>
        </form>

        {state === 'loading' ? (
          <SummaryCard>
            <StatusText>Reading every clause…</StatusText>
          </SummaryCard>
        ) : null}

        {state === 'offline' ? (
          <SummaryCard>
            <StatusText>
              Backend not configured — the lease checker needs the LLM endpoint
              to be reachable.
            </StatusText>
          </SummaryCard>
        ) : null}

        {state === 'error' ? <ErrorBanner>{errorMessage}</ErrorBanner> : null}

        {state === 'ready' && result !== null ? (
          <>
            <SummaryCard>
              <RiskRow>
                <RiskScoreBadge tone={riskTone(result.riskScore)}>
                  Risk score · {result.riskScore}/100
                </RiskScoreBadge>
                <RiskLabel>{riskLabel(result.riskScore)}</RiskLabel>
              </RiskRow>
              <SummaryText>{result.overallSummary}</SummaryText>
              <JurisdictionContext>
                {result.jurisdictionContext}
              </JurisdictionContext>
            </SummaryCard>

            {result.findings.length === 0 ? (
              <SummaryCard>
                <StatusText>
                  No specific clauses jumped out — the lease reads as pretty
                  standard.
                </StatusText>
              </SummaryCard>
            ) : (
              <FindingsList>
                {sortFindingsBySeverity(result.findings).map(
                  (finding, index) => (
                    <FindingCard
                      key={`${finding.category}-${index}`}
                      severity={finding.severity}
                    >
                      <FindingHeader>
                        <FindingCategory>{finding.category}</FindingCategory>
                        <SeverityPill severity={finding.severity}>
                          <span aria-hidden="true">
                            <Icon icon={severityGlyph(finding.severity)} />
                          </span>
                          {finding.severity}
                        </SeverityPill>
                      </FindingHeader>
                      <Excerpt>&ldquo;{finding.excerpt}&rdquo;</Excerpt>
                      <FindingExplanation>
                        {finding.explanation}
                      </FindingExplanation>
                      <NormalBlock>
                        <NormalLabel>What&apos;s typical</NormalLabel>
                        <NormalText>{finding.whatsNormal}</NormalText>
                      </NormalBlock>
                    </FindingCard>
                  ),
                )}
              </FindingsList>
            )}

            <Disclaimer>
              QuickPass lease checks are an AI second-pair-of-eyes — not legal
              advice. If a clause seems serious, talk to a tenant advocacy line
              or paralegal in your province.
            </Disclaimer>
          </>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default LeaseCheckPage
